/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import WireframeBase, { type ComponentStyles } from './WireframeBase';
import tools from '../tools';
import wireframeScrollBar from '../assets/icons/wireframeScrollBar.svg';
import wireframeStyleScrollBarVerticalRound from '../assets/icons/wireframeStyleScrollBarVerticalRound.svg';
import wireframeStyleScrollBarHorizontalRound from '../assets/icons/wireframeStyleScrollBarHorizontalRound.svg';
import wireframeStyleScrollBarVerticalSquare from '../assets/icons/wireframeStyleScrollBarVerticalSquare.svg';
import wireframeStyleScrollBarHorizontalSquare from '../assets/icons/wireframeStyleScrollBarHorizontalSquare.svg';
import { Rect } from '@lib/canvasx/components/Rect';
import { type RenderedDimensions } from '@lib/canvasx/components/FlexRow';
import ProgressBarControl from '../controls/ProgressBarControl';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import { WIREFRAME_PRIMARY_COLOR } from '../utils/wireframes';
import { FlexColumn } from '@lib/canvasx/components/FlexColumn';
import WireframeLinearResizeControl from '../controls/WireframeLinearResizeControl';

const BORDER_RADIUS = 64;
const MIN_VERTICAL_WIDTH = 7;
const MIN_VERTICAL_HEIGHT = 33;

class WireframeScrollBar extends WireframeBase {
  _scaledProgressValue: number;
  labelRowDimensions: RenderedDimensions;
  labelDimensions: RenderedDimensions;
  valueDimensions: RenderedDimensions;
  constructor(fabricObject) {
    super(fabricObject);
    this.labelRowDimensions = { width: 0, height: 0, rows: 0 };
    this.labelDimensions = { width: 0, height: 0, rows: 0 };
    this.valueDimensions = { width: 0, height: 0, rows: 0 };

    this.fabricObject.on('selected', () => this.updateProgressControl());

    this.updateDimensionsForLayout();
  }

  tools() {
    return [
      [tools.ToolWireframeComponentStyle],
      [tools.ToolScrollBarStyleFlags],
      [tools.ToolSize],
      [tools.ToolFillColorWithoutOpacity],
    ];
  }

  static defaultWidth(): number {
    return 12;
  }

  static defaultHeight(): number {
    return 216;
  }

  static iconSvg() {
    return wireframeScrollBar;
  }

  static externalName(): string {
    return 'Scroll bar';
  }

  static wireframeControls = this.createDefaultControls();
  static createDefaultControls() {
    const controls = super.createDefaultControls();
    return {
      ...controls,
      ml: new WireframeLinearResizeControl({ edge: 'left' }),
      mr: new WireframeLinearResizeControl({ edge: 'right' }),
      mt: new WireframeLinearResizeControl({ edge: 'top' }),
      mb: new WireframeLinearResizeControl({ edge: 'bottom' }),
    };
  }

  static componentStyles(): ComponentStyles {
    return {
      vertical_round: wireframeStyleScrollBarVerticalRound,
      horizontal_round: wireframeStyleScrollBarHorizontalRound,
      vertical_square: wireframeStyleScrollBarVerticalSquare,
      horizontal_square: wireframeStyleScrollBarHorizontalSquare,
    };
  }

  componentStyles(): ComponentStyles {
    return WireframeScrollBar.componentStyles();
  }

  get handleSize() {
    const { width, height } = this.fabricObject;
    return this.vertical ? height / 4 : width / 4;
  }

  get handleSizeRatio() {
    const { width, height } = this.fabricObject;
    const { handleSize, vertical } = this;

    // Get ratio of handle size to scrollbar size. Technically, this is the ratio
    // of half the handle to the overall size, but we use this to constrain the
    // progress control, which sits in the middle of the handle
    return vertical
      ? (height - handleSize / 2) / height
      : (width - handleSize / 2) / width;
  }

  get vertical(): boolean {
    return this.fabricObject.style?.toLowerCase().includes('vertical');
  }

  minDimension({ resizingWidth = true }) {
    const { height, width } = this.fabricObject;
    const { vertical } = this;
    let minWidth;
    let minHeight;
    if (vertical) {
      minWidth = MIN_VERTICAL_WIDTH;
      // Height can't get smaller than 5x width
      minHeight = Math.max(MIN_VERTICAL_HEIGHT, width * 5);
    } else {
      // Flip the constrains for horizontal scrollbars
      minWidth = Math.max(MIN_VERTICAL_HEIGHT, height * 5);
      minHeight = MIN_VERTICAL_WIDTH;
    }
    return resizingWidth ? minWidth : minHeight;
  }

  maxDimension({ resizingWidth = true }) {
    const { height, width } = this.fabricObject;
    const { vertical } = this;
    let max;
    if (vertical) {
      // Width cannot get larger than 20% of the height
      max = resizingWidth ? height * 0.2 : Number.MAX_SAFE_INTEGER;
    } else {
      // Flip the constrains for horizontal scrollbars
      max = resizingWidth ? Number.MAX_SAFE_INTEGER : width * 0.2;
    }
    return max;
  }

  // Constrain progress such that handle stays within bounds of scrollbar
  constrainProgress() {
    const adjustedPercentage = Math.min(
      Math.max(this.fabricObject.progress, 1 - this.handleSizeRatio),
      this.handleSizeRatio
    );

    this.fabricObject.set('progress', adjustedPercentage);
  }

  updateProgressControl() {
    // Chagne progress control to respect orientation. This will place it to the
    // right for vertcial scroll bars, and to the bottom for horizontal scroll bars
    const layout = this.vertical ? 'column' : 'row';
    if (!this.fabricObject.controls.progress) {
      this.fabricObject.controls.progress = new ProgressBarControl({ layout });
    }
    (this.fabricObject.controls.progress as ProgressBarControl).layout = layout;
  }

  updateDimensionsForLayout() {
    // When the style changes, swap width and height.
    if (this.vertical && this.fabricObject.height < this.fabricObject.width) {
      this.fabricObject.set({
        height: this.fabricObject.width,
        width: this.fabricObject.height,
      });
    } else if (
      !this.vertical &&
      this.fabricObject.width < this.fabricObject.height
    ) {
      this.fabricObject.set({
        width: this.fabricObject.height,
        height: this.fabricObject.width,
      });
    }

    // Change position control to respect orientation
    this.updateProgressControl();
  }

  wasSet(keyOrPath: KeyPath, value: any): void {
    super.wasSet(keyOrPath, value);

    if (keyOrPath === 'style') {
      this.updateDimensionsForLayout();
    }

    if (keyOrPath === 'progress') {
      // Limit progress to account for size of handle, such that handle stays
      // within bounds of scrollbar.
      this.constrainProgress();
    }
  }

  render(ctx: CanvasRenderingContext2D) {
    const {
      width,
      height,
      scaleX,
      progress = 0.5,
      showOutline,
    } = this.fabricObject;
    const { handleSize, round, vertical } = this;
    const fill = this.fabricObject.fill ?? WIREFRAME_PRIMARY_COLOR;
    const borderRadius = round ? BORDER_RADIUS : 0;
    const handleX = vertical ? 0 : progress * width - handleSize / 2;
    const handleY = vertical ? progress * height - handleSize / 2 : 0;

    withContext(
      {
        canvas: this.fabricObject.canvas,
        ctx,
        object: this.fabricObject,
        scale: scaleX,
      },
      () => {
        renderElement(
          <FlexColumn
            width={width}
            height={height}
            fill={{ color: fill, opacity: 0.08 }}
            border={showOutline ? { color: fill, opacity: 0.4 } : 'transparent'}
            borderRadius={borderRadius}
          >
            <Rect
              x={handleX}
              y={handleY}
              width={vertical ? width : width / 4}
              height={vertical ? height / 4 : height}
              fill={fill}
              borderRadius={borderRadius}
            />
          </FlexColumn>
        );
      }
    );
  }
}

export default WireframeScrollBar;
